import { useEffect, useMemo, useState } from 'react';
import { useLayoutSlots } from '@app/layouts/layoutContext';
import { useAppStore } from '@app/store/useAppStore';
import { useKey } from '@app/hooks/useKey';
import { useProgressions } from '@hooks/useProgressions';
import { PROGRESSIONS } from '@theory/progressions';
import type { ProgressionMood, ProgressionGenre } from '@theory/progressions';
import { displayModeName } from '@util/displayMode';
import { KeyModeSelector } from '@components/KeyModeSelector';
import { ProgressionCard } from '@components/ProgressionCard';
import { FilterChips } from '@components/FilterChips';
import styles from './ProgressionsRoute.module.css';

const MOODS: readonly ProgressionMood[] = [
  'happy', 'sad', 'tense', 'triumphant', 'dreamy', 'melancholic', 'bluesy', 'anthemic',
];
const GENRES: readonly ProgressionGenre[] = [
  'pop', 'rock', 'blues', 'jazz', 'folk', 'classical', 'country', 'r&b',
];

export function ProgressionsRoute() {
  const { setCenter, setRight } = useLayoutSlots();
  const keyId = useAppStore((s) => s.selectedKeyId);
  const useTraditional = useAppStore((s) => s.useTraditionalNames);
  const key = useKey(keyId);

  const [moods, setMoods] = useState<Set<ProgressionMood>>(new Set());
  const [genres, setGenres] = useState<Set<ProgressionGenre>>(new Set());

  const moodList = useMemo(() => Array.from(moods), [moods]);
  const genreList = useMemo(() => Array.from(genres), [genres]);

  const results = useProgressions({ key, moods: moodList, genres: genreList });

  const modeHasAny = useMemo(
    () => (key ? PROGRESSIONS.some((p) => p.modes.includes(key.mode)) : false),
    [key],
  );

  useEffect(() => {
    setCenter(
      <div className={styles.center}>
        <KeyModeSelector />
        <div className={styles.divider} />
        <div className={styles.filters}>
          <FilterChips<ProgressionMood> label="Mood" options={MOODS} selected={moods} onChange={setMoods} />
          <FilterChips<ProgressionGenre> label="Genre" options={GENRES} selected={genres} onChange={setGenres} />
        </div>
      </div>,
    );

    const modeName = key ? displayModeName(key.mode, useTraditional) : '';
    setRight(
      !key ? (
        <p className={styles.empty}>Pick a key on the Circle of Fifths.</p>
      ) : !modeHasAny ? (
        <p className={styles.empty}>{modeName} has very few documented progressions.</p>
      ) : results.length === 0 ? (
        <p className={styles.empty}>No progressions in {modeName} match those filters.</p>
      ) : (
        <div className={styles.list}>
          {results.map((p) => (
            <ProgressionCard key={p.id} progression={p} scaleKey={key} />
          ))}
        </div>
      ),
    );
    return () => {
      setCenter(null);
      setRight(null);
    };
  }, [results, moods, genres, key, modeHasAny, useTraditional, setCenter, setRight]);

  return null;
}
